import { LazyLoadImage } from "react-lazy-load-image-component";
import { Fragment, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import "react-lazy-load-image-component/src/effects/blur.css";

import { Field } from "@/lib/types";
import { FormLabel } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { request } from "@/lib/request";

export const FieldImage: Field["config"]["render"] = ({ field, title }) => {
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [imageSelectMode, setImageSelectMode] = useState<
    "empty" | "random" | "selected"
  >("random");
  const [selectedImage] = useState<string | null>(null);

  const handleImageSelectModeChange = (v: typeof imageSelectMode) => {
    if (v === "selected") {
      setImageSelectorOpen(true);
      return;
    }

    setImageSelectMode(v as typeof imageSelectMode);
    if (v === "empty") {
      field.onChange({ type: "empty" });
    } else if (v === "random") {
      field.onChange({ provider: "unsplash", type: "random" });
    }
  };

  const handleImageSelectorOpenChange = (v: boolean) => {
    setImageSelectorOpen(v);
    if (!v) {
      if (selectedImage) {
        setImageSelectMode("selected");
      } else {
        setImageSelectMode("random");
      }
    }
  };

  const handleConfirmImages = (
    type: "images" | "topic" | "random",
    value: string | string[],
  ) => {
    if (type === "random") {
      field.onChange({ provider: "unsplash", type });
    } else if (type === "images") {
      field.onChange({ provider: "unsplash", type, images: value });
    } else {
      field.onChange({ provider: "unsplash", type, topic: value });
    }
  };

  return (
    <>
      <FormLabel>{title}</FormLabel>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Image</Label>
        <Select
          value={field.value.type === "topic" ? "selected" : field.value.type}
          onValueChange={handleImageSelectModeChange}
        >
          <SelectTrigger className="w-[100px] text-xs">
            <SelectValue placeholder="Select Type" />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem value="empty">Empty</SelectItem>
            <SelectItem value="random">Random</SelectItem>
            <SelectItem value="selected">Selected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {imageSelectMode === "selected" ? (
        <div className="flex flex-col gap-2">
          <Label className="text-xs">Selected Image</Label>
          <div className="relative">
            <img className="aspect-square w-full rounded border border-border object-cover" />
            <Button variant="outline" className="absolute bottom-2 right-2">
              Change
            </Button>
          </div>
        </div>
      ) : null}
      <ImageDialog
        open={imageSelectorOpen}
        onOpenChange={handleImageSelectorOpenChange}
        handleConfirmImages={handleConfirmImages}
      />
    </>
  );
};

const ImageDialog = ({
  open,
  onOpenChange,
  handleConfirmImages,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  handleConfirmImages: (
    type: "images" | "topic",
    value: string | string[],
  ) => void;
}) => {
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  const onSelectTopic = (topic: string) => {
    onOpenChange(false);
    handleConfirmImages("topic", topic);
  };

  const onSelectImages = (images: string[]) => {
    onOpenChange(false);
    handleConfirmImages("images", images);
  };

  const onImageSelectorSelect = (topic: string) => {
    setSelectedTopic(topic);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!selectedTopic ? (
        <TopicSelector
          onSelectTopic={onSelectTopic}
          onImageSelectorSelect={onImageSelectorSelect}
        />
      ) : (
        <ImageSelector
          topicId={selectedTopic!}
          onSelectImages={onSelectImages}
          onPickTopic={() => onSelectTopic(selectedTopic!)}
          onPreviousStage={() => setSelectedTopic(null)}
        />
      )}
    </Dialog>
  );
};

const TopicSelector = ({
  onSelectTopic,
  onImageSelectorSelect,
}: {
  onSelectTopic: (topic: string) => void;
  onImageSelectorSelect: (topic: string) => void;
}) => {
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery<{
    data: any[];
    page: number;
    hasNextPage: boolean;
  }>({
    queryKey: ["unsplash-photos"],
    queryFn: async ({ pageParam }) => {
      const res = await request
        .get(`/external/unsplash/topics?page=${pageParam}`)
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to fetch topics.",
          );
        });

      return res.data.data;
    },
    retry: 3,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : null,
  });

  const handleSelectTopic = (id: string) => () => {
    setSelectedTopic(id);
  };

  return (
    <DialogContent className="w-full max-w-full lg:w-[1024px]">
      <DialogTitle>Select a topic or image</DialogTitle>
      <div className="relative mt-4 flex max-h-[calc(100vh-300px)] flex-col gap-4 overflow-y-auto">
        {status === "pending" ? (
          <div className="flex flex-wrap gap-4">
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
          </div>
        ) : error ? (
          <Alert>
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error.message ||
                "Failed to fetch images. Please try again later."}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex flex-wrap gap-4">
              {data.pages.map((group, i) => (
                <Fragment key={i}>
                  {group.data.map((topic: any) => (
                    <button
                      key={topic.id}
                      type="button"
                      className={cn(
                        "relative aspect-square w-[230px] flex-shrink-0 overflow-hidden rounded-xl border-4 border-transparent shadow",
                        { "border-cyan-500": selectedTopic === topic.id },
                      )}
                      onClick={handleSelectTopic(topic.id)}
                    >
                      <LazyLoadImage
                        alt={topic.alt}
                        height={240}
                        src={`${topic.cover_photo.urls.raw}&q=80&fm=jpg&crop=entropy&cs=tinysrgb&w=240&h=240&fit=crop`}
                        placeholderSrc={topic.cover_photo.urls.small_s3}
                        width={240}
                        effect="blur"
                        wrapperProps={{
                          style: { transitionDelay: "1s" },
                        }}
                      />
                      <div className="absolute bottom-0 left-0 flex w-full justify-start bg-gradient-to-b from-transparent to-black/50 p-4">
                        <span className="text-sm font-bold text-white drop-shadow-xl">
                          {topic.title}
                        </span>
                      </div>
                    </button>
                  ))}
                </Fragment>
              ))}
            </div>
            {hasNextPage ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? "Loading..." : "Load More"}
              </Button>
            ) : null}
          </>
        )}
      </div>
      <DialogFooter>
        {selectedTopic ? (
          <div className="mt-4 flex justify-end gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onImageSelectorSelect(selectedTopic)}
            >
              Select Photo from Topic
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => onSelectTopic(selectedTopic)}
            >
              Pick Random Images from Topic
            </Button>
          </div>
        ) : null}
      </DialogFooter>
    </DialogContent>
  );
};

const ImageSelector = ({
  topicId,
  onSelectImages,
  onPickTopic,
  onPreviousStage,
}: {
  topicId: string;
  onSelectImages: (images: string[]) => void;
  onPickTopic: () => void;
  onPreviousStage: () => void;
}) => {
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["unsplash-topic-photos", topicId],
    queryFn: async ({ pageParam }) => {
      const res = await request
        .get(`/external/unsplash/topics/${topicId}/photos?page=${pageParam}`)
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to fetch images.",
          );
        });

      return res.data.data;
    },
    retry: 3,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : null,
  });

  const handleImageSelect = (id: string) => () => {
    setSelectedImages(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      return [...prev, id];
    });
  };

  return (
    <DialogContent className="w-full max-w-full lg:w-[1024px]">
      <DialogTitle>Select a topic or image</DialogTitle>
      <div className="relative mt-4 flex max-h-[calc(100vh-300px)] flex-col gap-4 overflow-y-auto">
        {status === "pending" ? (
          <div className="flex flex-wrap gap-4">
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
            <Skeleton className="aspect-square w-[230px]" />
          </div>
        ) : error ? (
          <Alert>
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error.message ||
                "Failed to fetch images. Please try again later."}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex flex-wrap gap-4">
              {data.pages.map((group, i) => (
                <Fragment key={i}>
                  {group.data.map((image: any) => (
                    <button
                      key={image.id}
                      type="button"
                      className={cn(
                        "relative aspect-square w-[230px] flex-shrink-0 overflow-hidden rounded-xl border-4 border-transparent shadow",
                        {
                          "border-cyan-500": selectedImages.includes(image.id),
                        },
                      )}
                      onClick={handleImageSelect(image.id)}
                    >
                      <LazyLoadImage
                        alt={image.alt}
                        height={240}
                        src={`${image.urls.raw}&q=80&fm=jpg&crop=entropy&cs=tinysrgb&w=240&h=240&fit=crop`}
                        placeholderSrc={image.urls.small_s3}
                        width={240}
                        effect="blur"
                        wrapperProps={{
                          style: { transitionDelay: "1s" },
                        }}
                      />
                    </button>
                  ))}
                </Fragment>
              ))}
            </div>
            {hasNextPage ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? "Loading..." : "Load More"}
              </Button>
            ) : null}
          </>
        )}
      </div>
      <DialogFooter>
        <div className="mt-4 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={onPreviousStage}>
            Back
          </Button>
          {selectedImages.length ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => onSelectImages(selectedImages)}
            >
              Pick Selected Images
            </Button>
          ) : null}
          <Button type="button" variant="default" onClick={onPickTopic}>
            Pick Random Images from Topic
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
